import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, useModal } from "../../modal";
import {
  Button,
  TableContainer,
  Paper,
  IconButton,
  TableCell,
} from "@mui/material";
import * as XLSX from "xlsx";
import {
  GridContainer,
  GridItem,
  DataCard,
  DataCardHeader,
  DataCardTitle,
  ReportTable,
  ReportTableRow,
  ReportTableHeader,
  AmountCell,
  StatusCell,
} from "./PeriodicBillingReport.styles";
import { Receipt } from "@mui/icons-material";
import { ResetStore } from "./PeriodicBillingReport.slice";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import moment from "moment";
import {
  formatUtcToLocal,
  getLocalTzLabel,
} from "../../../services/dateUtils";

const tzLabel = getLocalTzLabel();

const PeriodicBillingReportResultModal = () => {
  const dispatch = useDispatch();
  const { close } = useModal();
  const { isOpen } = useSelector((state) => state.modal);
  const { reportData, totalRecords } = useSelector(
    (state) => state.periodicBillingReport
  );

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    close();
    dispatch(ResetStore());
  };

  const handleDownloadPDF = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return "-";
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return `$${numAmount.toFixed(2)}`;
  };

  const formatStatus = (status) => {
    if (!status) return "Pending";
    if (status === "E") return "Executed";
    if (status === "P") return "Pending";
    return status;
  };

  // UI: hora local del navegador.
  const formatDate = (dateString) => formatUtcToLocal(dateString) || "-";

  const formatClientName = (row) => {
    const parts = [];
    if (row.client_first_name) parts.push(row.client_first_name);
    if (row.client_last_name) parts.push(row.client_last_name);
    if (row.client_company_name) parts.push(`(${row.client_company_name})`);
    return parts.length > 0 ? parts.join(" ") : "-";
  };

  const handleDownloadExcel = () => {
    if (!reportData || reportData.length === 0) {
      return;
    }

    const dataToExport = reportData.map((row) => ({
      "Invoice #": row.invoice_number || "-",
      "Invoice Amount": row.invoice_amount
        ? parseFloat(row.invoice_amount).toFixed(2)
        : "-",
      "Client First Name": row.client_first_name || "-",
      "Client Last Name": row.client_last_name || "-",
      "Client Company": row.client_company_name || "-",
      "Transaction Status": formatStatus(row.transaction_status),
      "Exchange Code": row.exchange_code || "-",
      [tzLabel ? `Transaction Date (${tzLabel})` : "Transaction Date"]:
        row.transaction_updated_at
          ? formatUtcToLocal(row.transaction_updated_at)
          : "-",
      "Invoice PDF URL": row.invoice_file_url || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Billing Report");

    // Generate filename with date range
    const dateStr = moment().format("YYYY-MM-DD");
    const filename = `billing_range_report_${dateStr}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <Modal
      fullScreen
      actions={
        <>
          <Button
            onClick={handleDownloadExcel}
            variant="contained"
            color="primary"
            disabled={!reportData || reportData.length === 0}
          >
            Download Excel
          </Button>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        </>
      }
    >
      <GridContainer>
        <GridItem xs={12}>
          <DataCard>
            <DataCardHeader>
              <Receipt color="primary" />
              <DataCardTitle>
                Billing Report Results ({totalRecords} records)
              </DataCardTitle>
            </DataCardHeader>
            <TableContainer component={Paper}>
              <ReportTable>
                <ReportTableHeader>
                  <ReportTableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Invoice Amount</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Transaction Status</TableCell>
                    <TableCell>Exchange Code</TableCell>
                    <TableCell>
                      {tzLabel ? `Transaction Date (${tzLabel})` : "Transaction Date"}
                    </TableCell>
                    <TableCell align="center">Invoice PDF</TableCell>
                  </ReportTableRow>
                </ReportTableHeader>
                <tbody>
                  {reportData && reportData.length > 0 ? (
                    reportData.map((row, index) => (
                      <ReportTableRow key={row.invoice_id || index}>
                        <TableCell>{row.invoice_number || "-"}</TableCell>
                        <AmountCell>{formatAmount(row.invoice_amount)}</AmountCell>
                        <TableCell>{formatClientName(row)}</TableCell>
                        <StatusCell status={row.transaction_status}>
                          {formatStatus(row.transaction_status)}
                        </StatusCell>
                        <TableCell>{row.exchange_code || "-"}</TableCell>
                        <TableCell>{formatDate(row.transaction_updated_at)}</TableCell>
                        <TableCell align="center">
                          {row.invoice_file_url ? (
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadPDF(row.invoice_file_url)}
                              title="Download Invoice PDF"
                              color="primary"
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </ReportTableRow>
                    ))
                  ) : (
                    <ReportTableRow>
                      <TableCell colSpan={8} align="center">
                        No data available
                      </TableCell>
                    </ReportTableRow>
                  )}
                </tbody>
              </ReportTable>
            </TableContainer>
          </DataCard>
        </GridItem>
      </GridContainer>
    </Modal>
  );
};

export default PeriodicBillingReportResultModal;

